import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse, baseApiUrl, fetcher } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import { DTBBarChartResponse } from '../_types';
import { generateDTBTrends } from './riskPoolOrgDataGenerator';
import { useRiskPoolDemoData } from '../../../../_data_sources/useRiskPoolDemoData';

interface QueryParams {
  startDate: string;
  endDate: string;
  manualId?: string;
}

export const getDTBTrends = async (
  parentOrgId: string,
  childOrgId: string,
  queryParams: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<DTBBarChartResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${parentOrgId}/risk-pool/related-organizations/${childOrgId}/policy/dtb-trends`;

  const params = new URLSearchParams();
  if (queryParams.startDate) params.append('startDate', queryParams.startDate);
  if (queryParams.endDate) params.append('endDate', queryParams.endDate);

  const queryString = params.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const data: DTBBarChartResponse = await fetcher({
      method: 'get',
      url: fullUrl,
      options: { signal },
    });

    return data;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useDTBTrends(
  childOrgId: string,
  queryParams: QueryParams,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId; // Fallback to childOrgId if not available
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<DTBBarChartResponse, Error>(
    [
      'risk_pool_org_dtb_trends',
      parentOrgId,
      childOrgId,
      queryParams.startDate,
      queryParams.endDate,
      queryParams.manualId,
    ],
    async ({ signal }): Promise<DTBBarChartResponse> => {
      try {
        if (isDemoDataEnabled) {
          return generateDTBTrends(childOrgId, queryParams.startDate, queryParams.endDate);
        }

        return await getDTBTrends(parentOrgId, childOrgId, queryParams, signal, onError);
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!queryParams.startDate && !!queryParams.endDate,
    }
  );
}
